import { ExpandedGalleryVisualPopup, ExpandedVisualSwitchDirection } from 'components/common/ExpandedVisualPopup'
import { FC, useCallback, useMemo, useState } from 'react'
import { useAddFavoriteVisual, useDeleteFavoriteVisual, useGetAssignmentVisualByTypeName } from 'dataQueries'
import { useGalleryAdditionalVisuals, useGalleryAssignment, useGalleryDeal, useGalleryVisualSelection, useGalleryVisualType, useGalleryVisuals, useGalleryVisualsDownloadArchive, useGalleryVisualsMeta } from '../../contexts'
import { useLocation, useNavigate, useParams } from 'react-router'

import { AnalyticsEvent } from 'utils/analytics'
import { ClientGalleryFloatingToolBar } from '../ClientGalleryFloatingToolBar'
import { ClientGalleryPhotoSection } from '../ClientGalleryPhotoSection'
import { GalleryUpsellPayment } from 'components/common/Gallery/GalleryUpsellPayment'
import { GalleryVisualDownloadPopup } from 'components/common/Gallery/GalleryVisualDownloadPopup'
import { Path } from 'constants/router'
import { RealEstatePropertyDTO } from 'models/property'
import { VisualFileType } from 'constants/visual'
import classNames from 'classnames'
import { getPropertyPathname } from 'utils/helpers/getPropertyPathname'
import { logger } from 'utils/logging'
import styles from './ClientGalleryPhoto.module.sass'
import { useClientGallery } from '../_main/contexts'
import { useDebouncedMemo } from 'utils/helpers'
import { useImageRegex } from 'utils/hooks'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

enum UpdateFavoriteType {
  DELETE = 'DELETE',
  ADD = 'ADD',
}

export enum ExpandedVisualType {
  ORIGINAL = 'ORIGINAL',
  EDITED = 'EDITED',
}

/**
 * @interface Props for the ClientGalleryPhoto component.
 */
interface Props {
  /** Property data for property flow - optional for gallery flow */
  propertyData?: RealEstatePropertyDTO | undefined
}

/**
 * Client gallery photo content.
 * 
 * @example <ClientGalleryPhoto />
 */
export const ClientGalleryPhoto: FC<Props> = ({ propertyData }) => {

  const { pathname } = useLocation()
  const { id } = useParams<{ id: string }>()
  const { webType } = useGalleryVisualType()
  const { initiateArchiveDownload } = useGalleryVisualsDownloadArchive()
  const { t } = useTranslation(['gallery', 'visual_loading'])
  const navigate = useNavigate()

  const { dealData } = useGalleryDeal()
  const { userPropertyWorkspaceId } = useUserData()
  const {
    downloadVisuals,
    downloadVisualsKeys,
    downloadVisualsCount,
    favoritedVisuals,
    visualMetadataMap,
    setFavoritedVisuals
  } = useGalleryVisuals()

  const {
    purchasedVisualsKeys,
    unPurchasedVisualsKeys,
    prepurchasedVisualsRemaining,
  } = useGalleryVisualsMeta()

  const {
    selected,
    selectedNotPurchasedVisualsKeys,
    selectedNotPurchasedVisualsCount,
    toggleSelectOnImage,
  } = useGalleryVisualSelection()

  const { isAdditionalVisualPurchaseDisabled } = useGalleryAdditionalVisuals()

  const {
    assignmentId,
    setIsToolBarOpen,
  } = useClientGallery()

  const {
    logGalleryEvent,
  } = useGalleryAssignment()

  const { spawnErrorToast } = useSnackbar()

  const imageNameReplacePattern = useImageRegex(assignmentId)

  const addFavoriteVisual = useAddFavoriteVisual()
  const deleteFavoriteVisual = useDeleteFavoriteVisual()
  const assignmentVisualByName = useGetAssignmentVisualByTypeName()

  const [isExpandedVisualOpen, setIsExpandedVisualOpen] = useState<boolean>(false)
  const [expandedVisualName, setExpandedVisualName] = useState<string | undefined>(undefined)
  const [expandedVisualType, setExpandedVisualType] = useState<ExpandedVisualType>(ExpandedVisualType.EDITED)

  const isPropertyPathname = useMemo(() => getPropertyPathname(pathname), [pathname])

  const expandedVisualMetadata = useMemo(() => (expandedVisualName && visualMetadataMap[expandedVisualName]) ?? null, [visualMetadataMap, expandedVisualName])

  const expandedVisual = useMemo(() => {
    if (!expandedVisualName) return undefined
    if (!downloadVisuals?.[expandedVisualName]) return undefined

    const isPurchasedVisuals = purchasedVisualsKeys.has(expandedVisualName)
    const visualWebType = isPurchasedVisuals ? VisualFileType.POST_WEB : webType

    return downloadVisuals[expandedVisualName]?.[visualWebType]
  }, [downloadVisuals, expandedVisualName, purchasedVisualsKeys, webType])

  const expandedAiSourceVisual = useMemo(() => {
    if (!expandedVisualName) return undefined

    const aiSourceFilename = expandedVisualMetadata && expandedVisualMetadata.aiSourceFilename
    if (!aiSourceFilename || !downloadVisuals?.[aiSourceFilename]) return undefined

    return downloadVisuals[aiSourceFilename]?.[VisualFileType.POST_WEB]
  }, [downloadVisuals, expandedVisualMetadata, expandedVisualName])

  const expandedImageName = useMemo(() => expandedVisual?.file?.name, [expandedVisual?.file?.name])
  const expandedAiSourceImageName = useMemo(() => expandedAiSourceVisual?.file?.name, [expandedAiSourceVisual?.file?.name])

  const navigateToEditing = (
    file: File,
    imageUrl: string,
    isSelfEditedVisual: boolean
  ) => {
    const propertyId = dealData?.property?.id || ''

    if (isPropertyPathname) {
      if (id && assignmentId) {
        navigate(Path.PROPERTY_ASSIGNMENT_PHOTO_STUDIO.replace(':id', id.toString()).replace(':assignmentId', assignmentId.toString()), { state: { file, imageUrl, assignmentId, isSelfEditedVisual } })
      }
    } else if (assignmentId) {
      if (propertyId) {
        return navigate(Path.PROPERTY_ASSIGNMENT_PHOTO_STUDIO.replace(':id', propertyId).replace(':assignmentId', assignmentId.toString()), { state: { file, imageUrl, assignmentId, isSelfEditedVisual } })
      }
      navigate(Path.GALLERY_VISUAL_PHOTO_STUDIO.replace(':id', assignmentId), {
        state: { file, imageUrl, assignmentId, isSelfEditedVisual, propertyId }
      })
    }
  }

  const fetchImageUrl = useCallback(async (visualName: string): Promise<string> => {
    try {
      const response = await assignmentVisualByName.mutateAsync({
        assignmentId,
        workspaceId: userPropertyWorkspaceId,
        visualType: VisualFileType.POST_WEB,
        visualName,
      })

      if (response.data.url) {
        return response.data.url
      }
    } catch (error) {
      logger.error('Error during image loading', error, {
        context: {
          component: 'ClientGalleryPhoto',
          action: 'fetchImageUrl',
          metadata: {
            visualName,
            assignmentId,
            visualType: VisualFileType.POST_WEB,
          }
        }
      })

      spawnErrorToast(t('visual_loading:general_error', { error }))
    }
    return ''

  }, [assignmentVisualByName, assignmentId, userPropertyWorkspaceId, spawnErrorToast, t])

  const onOpenPhotoStudio = useCallback(async (file: File, isSelfEditedVisual: boolean) => {
    const imageUrl = await fetchImageUrl(file.name)

    navigateToEditing(file, imageUrl, isSelfEditedVisual)
    // ignore the eslint warning for the dependency array - navigateToEditing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchImageUrl])

  const onUpdateFavoriteVisuals = useDebouncedMemo(() =>
    (type: UpdateFavoriteType, filename: string) => {
      switch (type) {
        case UpdateFavoriteType.ADD:
          addFavoriteVisual.mutate({ assignmentId, workspaceId: userPropertyWorkspaceId, visuals: [filename] }, {
            onError: () => {
              favoritedVisuals.delete(filename)
              setFavoritedVisuals(new Set(favoritedVisuals))
            }
          })
          break
        case UpdateFavoriteType.DELETE:
          deleteFavoriteVisual.mutate({ assignmentId, workspaceId: userPropertyWorkspaceId, visuals: [filename] }, {
            onError: () => {
              favoritedVisuals.add(filename)
              setFavoritedVisuals(new Set(favoritedVisuals))
            }
          })
          break

        default:
          break
      }
    }
    , [], 500, 700)

  const isFavoriteVisualsDisabled = addFavoriteVisual.isPending || deleteFavoriteVisual.isPending

  const toggleFavoriteOnImage = useCallback((filename?: string) => {
    if (!filename) return
    if (isFavoriteVisualsDisabled) return

    if (favoritedVisuals.has(filename)) {
      favoritedVisuals.delete(filename)
      setFavoritedVisuals(new Set(favoritedVisuals))
      onUpdateFavoriteVisuals(UpdateFavoriteType.DELETE, filename)
      logGalleryEvent(AnalyticsEvent.GALLERY_UNMARK_FAVORITE)
    } else {
      favoritedVisuals.add(filename)
      setFavoritedVisuals(new Set(favoritedVisuals))
      onUpdateFavoriteVisuals(UpdateFavoriteType.ADD, filename)
      logGalleryEvent(AnalyticsEvent.GALLERY_MARK_FAVORITE)
    }
  }, [favoritedVisuals, onUpdateFavoriteVisuals, logGalleryEvent, setFavoritedVisuals, isFavoriteVisualsDisabled])

  const switchExpandedImage = useCallback((direction: ExpandedVisualSwitchDirection) => {
    if (!expandedVisualName) return
    if (!downloadVisuals) return

    const isDirectionNext = direction === ExpandedVisualSwitchDirection.RIGHT

    if (downloadVisualsCount === 0) return
    let chooseThisOne = false

    setExpandedVisualType(ExpandedVisualType.EDITED)

    for (let i = isDirectionNext ? 0 : downloadVisualsCount - 1; isDirectionNext ? i < downloadVisualsCount : i >= 0; isDirectionNext ? i++ : i--) {
      if (chooseThisOne) return setExpandedVisualName(downloadVisualsKeys[i])
      if (expandedVisualName === downloadVisualsKeys[i]) chooseThisOne = true
    }

    return setExpandedVisualName(isDirectionNext ? downloadVisualsKeys[0] : downloadVisualsKeys[downloadVisualsCount - 1])
  }, [expandedVisualName, downloadVisuals, downloadVisualsCount, downloadVisualsKeys])

  const getExpandedVisual = useMemo(() => {
    if (!expandedVisualMetadata) return undefined
    if (!expandedVisualMetadata.aiSourceFilename) return expandedVisual

    switch (expandedVisualType) {
      case ExpandedVisualType.ORIGINAL:
        return expandedAiSourceVisual
      case ExpandedVisualType.EDITED:
        return expandedVisual
      default:
        return expandedVisual
    }

  }, [expandedAiSourceVisual, expandedVisual, expandedVisualMetadata, expandedVisualType])

  const getExpandedVisualName = useMemo(() => {
    if (!expandedVisualMetadata) return undefined
    if (!expandedVisualMetadata.aiSourceFilename) return expandedImageName

    switch (expandedVisualType) {
      case ExpandedVisualType.ORIGINAL:
        return expandedAiSourceImageName
      case ExpandedVisualType.EDITED:
        return expandedImageName
      default:
        return expandedImageName
    }

  }, [expandedAiSourceImageName, expandedImageName, expandedVisualMetadata, expandedVisualType])

  const switchExpandedVisualType = useCallback((type: ExpandedVisualType) => {
    setExpandedVisualType(type)
  }, [])

  const onEditFile = useMemo(() => getExpandedVisual?.file ? getExpandedVisual.file : new File([], getExpandedVisualName ?? ''), [getExpandedVisual?.file, getExpandedVisualName])
  const isSelfEditedVisual = useMemo(() => visualMetadataMap[getExpandedVisual?.file?.name ?? '']?.selfEdited, [getExpandedVisual?.file?.name, visualMetadataMap])

  return (
    <div className={classNames(styles.clientGalleryPhoto)}>

      <ClientGalleryPhotoSection
        favorited={favoritedVisuals}
        setIsExpandedVisualOpen={setIsExpandedVisualOpen}
        setExpandedVisualName={setExpandedVisualName}
        toggleFavoriteOnImage={toggleFavoriteOnImage}
        isFavoriteDisabled={isFavoriteVisualsDisabled}
        visualsMetadata={visualMetadataMap}
        onOpenPhotoStudio={onOpenPhotoStudio}
      />

      <ClientGalleryFloatingToolBar propertyData={propertyData} />

      <GalleryVisualDownloadPopup />

      <GalleryUpsellPayment />

      <ExpandedGalleryVisualPopup
        isOpen={isExpandedVisualOpen}
        isSelectable={!(isAdditionalVisualPurchaseDisabled && unPurchasedVisualsKeys.has(getExpandedVisualName ?? '') && !selectedNotPurchasedVisualsKeys.has(getExpandedVisualName ?? '') && selectedNotPurchasedVisualsCount >= prepurchasedVisualsRemaining)}
        isFavoriteSelectable
        isSelected={selected.has(getExpandedVisualName ?? '')}
        isFavorite={favoritedVisuals.has(getExpandedVisualName ?? '')}
        isPurchased={purchasedVisualsKeys.has(getExpandedVisualName ?? '')}
        visualMetadata={visualMetadataMap[expandedImageName ?? ''] ?? null}
        signedUrl={getExpandedVisual?.signedUrl}
        filename={expandedImageName}
        label={expandedImageName?.replace(imageNameReplacePattern, '')}
        switchExpandedVisual={switchExpandedImage}
        switchExpandedVisualType={switchExpandedVisualType}
        expandedVisualType={expandedVisualType}
        toggleFavoriteOnImage={toggleFavoriteOnImage}
        onClose={() => setIsExpandedVisualOpen(false)}
        onSelect={() => {
          setIsToolBarOpen(true)
          toggleSelectOnImage(getExpandedVisualName)
        }}
        onDonwload={() => initiateArchiveDownload(new Set<string>([getExpandedVisualName ?? '']))}
        onOpenPhotoStudio={() => onOpenPhotoStudio(onEditFile, isSelfEditedVisual)}
      />

    </div>
  )
}
